import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { UserRole } from '@/hooks/useCurrentUser';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePlatform } from '@/hooks/usePlatform';
import { USE_MOCK } from '@/api/client';
import { MOCK_USERS } from '@/data/mockUsers';
import { SUPPORTED_LANGUAGE_CODES } from '@/i18n/languages';

export interface DemoUser {
  nombre: string;
  correo: string;
  /** AAD Object ID simulado — GUID estable generado una vez y guardado en localStorage. */
  userId: string;
  ou: string;
  do_: string;
  bu: string;
  departamento: string;
  puesto: string;
  numeronomina: string;
  fechaNacimiento: string;
  tenant: 'LATAM' | 'Europa';
  role: UserRole;
  /** Language code the user's Teams/SSO context would provide (es/en/pt/fr/de/nl/ro) */
  idioma: string;
}

// Maps admin.roleKey → UserRole used in auth/routing
export const ROLE_KEY_TO_ROLE: Record<string, UserRole> = {
  'admin.roleAdminCentral': 'AdminGlobal',
  'admin.roleAdminOU': 'AdminOU',
  'admin.roleEmployee': 'Employee',
};

// Feature flags — set to false to hide a view from ALL roles until the phase is released
export const FEATURES: Record<string, boolean> = {
  executive: false, // Phase 2 — hidden in Phase 1 release
};

// Views accessible per role (used by Sidebar + App to control visibility)
export const VIEW_ACCESS: Record<string, UserRole[]> = {
  survey:    ['Employee', 'AdminOU', 'AdminGlobal'],
  safe:      ['Employee', 'AdminOU', 'AdminGlobal'],
  admin:     ['AdminOU', 'AdminGlobal'],
  executive: ['AdminOU', 'AdminGlobal'],
};

export function canAccessView(view: string, role: UserRole): boolean {
  if (FEATURES[view] === false) return false;
  return (VIEW_ACCESS[view] ?? ['AdminGlobal']).includes(role);
}

function generateUserId(): string {
  // Genera un GUID v4 estable que imita un AAD Object ID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.floor(Date.now() * Math.random()) + Math.floor(Math.random() * 16)) % 16;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const DEFAULT_USER: DemoUser = {
  nombre: '',
  correo: '',
  userId: generateUserId(),
  ou: '',
  do_: '',
  bu: '',
  departamento: '',
  puesto: '',
  numeronomina: '',
  fechaNacimiento: '',
  tenant: 'LATAM',
  role: 'Employee',
  idioma: 'es',
};

const STORAGE_KEY = 'sigma-demo-user';

const VALID_ROLES: UserRole[] = ['Employee', 'AdminOU', 'AdminGlobal'];
const VALID_TENANTS: DemoUser['tenant'][] = ['LATAM', 'Europa'];

const SUPPORTED_UI_LANGUAGES = SUPPORTED_LANGUAGE_CODES;

/**
 * Precedencia del idioma inicial de un usuario real (MSAL): su preferredLanguage guardado en
 * el perfil backend gana siempre que sea uno de los 7 idiomas soportados; si no viene o no es
 * soportado, se usa el locale de Teams si es soportado; si tampoco, el idioma del navegador si
 * es soportado; si tampoco, inglés.
 */
export function resolveInitialLanguage(
  preferredLanguage: string | undefined,
  teamsLocale: string | undefined,
  browserLanguage: string,
): string {
  if (preferredLanguage && SUPPORTED_UI_LANGUAGES.includes(preferredLanguage)) {
    return preferredLanguage;
  }
  const normalizedTeamsLang = (teamsLocale ?? '').split('-')[0];
  if (SUPPORTED_UI_LANGUAGES.includes(normalizedTeamsLang)) {
    return normalizedTeamsLang;
  }
  const normalizedBrowserLang = browserLanguage.split('-')[0];
  if (SUPPORTED_UI_LANGUAGES.includes(normalizedBrowserLang)) {
    return normalizedBrowserLang;
  }
  return 'en';
}

function sanitizeStoredUser(raw: unknown): DemoUser {
  if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_USER };
  const u = raw as Record<string, unknown>;
  return {
    ...DEFAULT_USER,
    nombre:         typeof u['nombre'] === 'string'         ? u['nombre']         : DEFAULT_USER.nombre,
    correo:         typeof u['correo'] === 'string'         ? u['correo']         : DEFAULT_USER.correo,
    userId:         typeof u['userId'] === 'string' && u['userId'] ? u['userId'] : generateUserId(),
    ou:             typeof u['ou'] === 'string'             ? u['ou']             : DEFAULT_USER.ou,
    do_:            typeof u['do_'] === 'string'            ? u['do_']            : DEFAULT_USER.do_,
    bu:             typeof u['bu'] === 'string'             ? u['bu']             : DEFAULT_USER.bu,
    departamento:   typeof u['departamento'] === 'string'   ? u['departamento']   : DEFAULT_USER.departamento,
    puesto:         typeof u['puesto'] === 'string'         ? u['puesto']         : DEFAULT_USER.puesto,
    numeronomina:   typeof u['numeronomina'] === 'string'   ? u['numeronomina']   : DEFAULT_USER.numeronomina,
    fechaNacimiento:typeof u['fechaNacimiento'] === 'string'? u['fechaNacimiento']: DEFAULT_USER.fechaNacimiento,
    tenant:         VALID_TENANTS.includes(u['tenant'] as DemoUser['tenant']) ? u['tenant'] as DemoUser['tenant'] : DEFAULT_USER.tenant,
    role:           VALID_ROLES.includes(u['role'] as UserRole)               ? u['role'] as UserRole             : DEFAULT_USER.role,
    idioma:         typeof u['idioma'] === 'string'         ? u['idioma']         : DEFAULT_USER.idioma,
  };
}

function loadFromStorage(): DemoUser {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_USER };
    return sanitizeStoredUser(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_USER };
  }
}

interface UserContextValue {
  demoUser: DemoUser;
  setDemoUser: (u: DemoUser) => void;
  /** Returns detected UserRole if email matches a mock user, null otherwise */
  detectRoleByEmail: (email: string) => UserRole | null;
  /** Returns the full mock user record if email matches */
  findMockUser: (email: string) => typeof MOCK_USERS[number] | undefined;
}

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
  const [demoUser, setDemoUserState] = useState<DemoUser>(() => loadFromStorage());
  const currentUser = useCurrentUser();
  const { teamsContext } = usePlatform();

  useEffect(() => {
    if (USE_MOCK || !currentUser) return;
    // MSAL real: la identidad de Azure AD SIEMPRE manda (sobreescribe cualquier demo user previo
    // guardado en localStorage). userId = oid real; roles desde la BD (currentUser.primaryRole).
    setDemoUserState(prev => {
      const updated: DemoUser = {
        ...prev,
        userId: currentUser.id || prev.userId,
        nombre: currentUser.displayName || prev.nombre,
        correo: currentUser.email || prev.correo,
        // NOTA (Task 11 audit): este 'europa' NO es un slug de OU del catálogo (OU_CATALOG/useOUs) —
        // es el heurístico para resolver a qué TENANT de backend ('LATAM' | 'Europa', dos despliegues
        // distintos) pertenece el usuario real de MSAL, a partir de su dominio de correo. useOUs()
        // necesita `demoUser.tenant` para poder pedir las OUs (getOUs(tenantId)), así que no se puede
        // sustituir por `useGdprOuKey()` aquí sin crear una dependencia circular. Fuera de alcance de
        // este plan de refactor de OUs; queda documentado para no confundirlo con el literal 'europa'
        // de OU ya corregido en useOUs.ts/Task 7.
        tenant: currentUser.email?.toLowerCase().includes('europa') ? 'Europa' : prev.tenant,
        role: currentUser.primaryRole,
        idioma: resolveInitialLanguage(currentUser.preferredLanguage, teamsContext?.locale, navigator.language || ''),
      };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch { /* noop */ }
      return updated;
    });
  }, [currentUser, teamsContext]);

  function setDemoUser(u: DemoUser): void {
    setDemoUserState(u);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    } catch { /* storage unavailable in this environment */ }
  }

  function detectRoleByEmail(email: string): UserRole | null {
    const match = MOCK_USERS.find(
      u => u.email.toLowerCase() === email.toLowerCase().trim(),
    );
    if (!match) return null;
    return ROLE_KEY_TO_ROLE[match.roleKey] ?? 'Employee';
  }

  function findMockUser(email: string) {
    return MOCK_USERS.find(
      u => u.email.toLowerCase() === email.toLowerCase().trim(),
    );
  }

  return (
    <UserContext.Provider value={{ demoUser, setDemoUser, detectRoleByEmail, findMockUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUserContext must be used within <UserProvider>');
  return ctx;
}
